import {
  Avatar,
  colors,
  Icon,
  IconButton,
  Tag,
  Tooltip,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useCallback, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { type SelectedApprover, type Approver } from '../../../types';
import { SelectApproverDropdown } from '../../SelectApproverDropdown';

type Props = {
  approver: Approver;
  onDelete: () => void;
  isAllFollowingMode: boolean;
  index: number;
  onUpdateRule: (selectedApprover: SelectedApprover) => void;
};

export const ApproversListItem = ({
  approver,
  onDelete,
  isAllFollowingMode,
  index,
  onUpdateRule,
}: Props) => {
  const { t } = useTranslation('global');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleApproverSelect = (selectedApprover: SelectedApprover) => {
    onUpdateRule(selectedApprover);
    setIsDropdownOpen(false);
  };

  const handleButtonClick = useCallback(() => {
    setIsDropdownOpen((previous) => !previous);
  }, []);

  return (
    <>
      {isDropdownOpen ? (
        <SelectApproverDropdown
          onSelect={handleApproverSelect}
          currentApproverId={
            approver.approverType === 'user' ||
            approver.approverType === 'costCenterOwner'
              ? approver.id
              : approver.approverType
          }
          onBlur={() => setIsDropdownOpen(false)}
          className="mx-8 my-[5px]"
        />
      ) : (
        <div
          key={
            approver.approverType === 'user' ||
            approver.approverType === 'costCenterOwner'
              ? approver.id
              : approver.approverType
          }
          className={classNames(
            'ApproversList__item',
            isAllFollowingMode && 'ApproversList__item--all-following',
          )}
        >
          {isAllFollowingMode && (
            <div className="ApproversList__item__details__index">
              {index + 1}
            </div>
          )}
          <ApproverDisplay approver={approver} />
          <div className="ApproversList__item__icons">
            <IconButton
              iconName="pen"
              variant="tertiaryNeutral"
              onClick={handleButtonClick}
              data-testid="ApproversList__edit-approver"
              aria-label={t('misc.openMenu')}
            />
            <IconButton
              iconName="trash"
              variant="tertiaryNeutral"
              onClick={() => {
                track(
                  AnalyticEventName.APPROVALS_WORKFLOW_SETUP_DELETE_APPROVER,
                  {
                    approverId:
                      approver.approverType === 'user'
                        ? approver.id
                        : undefined,
                    approverType: approver.approverType,
                  },
                );
                onDelete();
              }}
              data-testid="ApproversList__delete-approver"
              aria-label={t('misc.delete')}
            />
          </div>
        </div>
      )}
    </>
  );
};

const ApproverDisplay = ({ approver }: { approver: Approver }) => {
  const { t } = useTranslation('global');

  if (approver.approverType === 'accountOwner') {
    return (
      <div className="flex w-full cursor-default gap-8">
        <div className="flex h-32 w-32 shrink-0 items-center justify-center self-center rounded-[50%] bg-background-secondary-brand-default">
          <Icon name="person" color={colors.backgroundPrimaryDefault} />
        </div>
        <div className="flex items-center gap-8">
          <div className="ApproversList__item__details__name text-content-primary title-m">
            {t('teams.approvalFlows.accountOwner')}
          </div>
        </div>
      </div>
    );
  }

  if (approver.approverType === 'reportingManager') {
    return (
      <div className="flex w-full cursor-default gap-8">
        <div className="flex h-32 w-32 shrink-0 items-center justify-center self-center rounded-[50%] bg-background-secondary-brand-default">
          <Icon name="person" color={colors.backgroundPrimaryDefault} />
        </div>
        <div>
          <div className="flex items-center gap-8">
            <div className="ApproversList__item__details__name text-content-primary title-m">
              {t('teams.approvalFlows.reportingManager')}
            </div>
            <Tag variant="purple">{t('misc.new')}</Tag>
          </div>
          <div className="mt-4 max-w-[220px] text-left text-content-primary body-s">
            {t('teams.approvalFlows.reportingManagerDisclaimer')}
          </div>
        </div>
      </div>
    );
  }

  if (approver.approverType === 'costCenterOwner') {
    return (
      <div className="flex w-full cursor-default gap-8">
        <div className="flex h-32 w-32 shrink-0 items-center justify-center self-center rounded-[50%] bg-background-secondary-brand-default">
          <Icon name="person" color={colors.backgroundPrimaryDefault} />
        </div>
        <div className="flex items-center gap-8">
          <div className="ApproversList__item__details__name text-content-primary title-m">
            {t('teams.approvalFlows.costCenterOwner')}
          </div>
        </div>
      </div>
    );
  }

  if (approver.approverType !== 'user') {
    return;
  }

  const label = approver.isPending ? approver.email : approver.fullname;
  const avatar = approver.avatar ?? undefined;
  const shouldDisplayTooltip = !getIsCostCenterOwner(approver);

  return (
    <Tooltip isDisabled={shouldDisplayTooltip} content={label}>
      <div className="flex w-full cursor-default gap-8">
        <Avatar src={avatar} text={label} className="shrink-0" />
        <div className="flex items-center gap-8">
          {getIsCostCenterOwner(approver) ? (
            <div className="ApproversList__item__details__name text-content-primary title-m">
              {t('teams.approvalFlows.costCenterOwner')}
            </div>
          ) : (
            <div className="ApproversList__item__details__name">{label}</div>
          )}

          {approver.isAccountOwner && (
            <div className="text-content-primary">
              ({t('profile.roleTitleAccountOwner')})
            </div>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

const getIsCostCenterOwner = (approver: Approver) => {
  return approver.approverType === 'costCenterOwner';
};
